import axios from "axios";
import { API_URL } from "../constants";

export interface Reservation {
  id: string;
  order: string;
  createdBy?: { id: string; name: string; email?: string } | null;
  status: ReservationStatus;
  number: number;
  notes?: string | null;
  customerFillsDelivery?: boolean;
  items?: ReservationItem[];
  createdAt?: number;
  updatedAt?: number;
}

export interface ReservationItem {
  id: string;
  reservation: string;
  product: string | { id: string; title?: string; sku?: string; supplier_sku?: string };
  warehouse?: string | { id: string; name?: string } | null;
  quantity: number;
  fulfilledQuantity?: number;
  orderLineType?: string | null;
  orderLineId?: string | null;
  notes?: string | null;
}

export type ReservationStatus = "draft" | "confirmed" | "cancelled";

export interface CreateReservationInput {
  status?: ReservationStatus;
  notes?: string;
  customerFillsDelivery?: boolean;
  items?: {
    product: string;
    warehouse?: string | null;
    quantity: number;
    orderLineType?: string | null;
    orderLineId?: string | null;
    notes?: string | null;
  }[];
}

export interface UpdateReservationInput {
  status?: ReservationStatus;
  notes?: string;
  customerFillsDelivery?: boolean;
  items?: {
    id?: string;
    product: string;
    warehouse?: string | null;
    quantity: number;
    orderLineType?: string | null;
    orderLineId?: string | null;
    notes?: string | null;
  }[];
}

export interface FetchReservationsResponse {
  reservations: Reservation[];
}

export interface StockWarning {
  product: string;
  warehouse: string;
  requested: number;
  available: number;
}

export interface ReservationResponse {
  reservation: Reservation;
  warnings?: StockWarning[];
}

export interface DeleteResponse {
  success: boolean;
  message: string;
}

// ============ RESERVATIONS ============

// Fetch all reservations for a company
export const fetchReservations = async (
  companyId: string,
  filters?: { status?: string; orderId?: string; customerId?: string }
): Promise<FetchReservationsResponse> => {
  let url = `${API_URL}/${companyId}/reservations`;
  if (filters) {
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.orderId) params.append("orderId", filters.orderId);
    if (filters.customerId) params.append("customerId", filters.customerId);
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
  }
  const response = await axios.get(url);
  return response.data;
};

// Fetch reservations for a specific order
export const fetchReservationsByOrder = async (
  companyId: string,
  orderId: string
): Promise<FetchReservationsResponse> => {
  const url = `${API_URL}/${companyId}/orders/${orderId}/reservations`;
  const response = await axios.get(url);
  return response.data;
};

// Fetch single reservation by ID
export const fetchReservationById = async (
  companyId: string,
  reservationId: string
): Promise<ReservationResponse> => {
  const url = `${API_URL}/${companyId}/reservations/${reservationId}`;
  const response = await axios.get(url);
  return response.data;
};

// Create a new reservation
export const createReservation = async (
  companyId: string,
  orderId: string,
  data: CreateReservationInput
): Promise<ReservationResponse> => {
  const url = `${API_URL}/${companyId}/orders/${orderId}/reservations`;
  const response = await axios.post(url, data);
  return response.data;
};

// Update a reservation
export const updateReservation = async (
  companyId: string,
  reservationId: string,
  data: UpdateReservationInput
): Promise<ReservationResponse> => {
  const url = `${API_URL}/${companyId}/reservations/${reservationId}`;
  const response = await axios.patch(url, data);
  return response.data;
};

// Delete a reservation (draft only)
export const deleteReservation = async (
  companyId: string,
  reservationId: string
): Promise<DeleteResponse> => {
  const url = `${API_URL}/${companyId}/reservations/${reservationId}`;
  const response = await axios.delete(url);
  return response.data;
};

// ============ WAREHOUSES ============

export interface Warehouse {
  id: string;
  name: string;
  code?: string;
  company?: string;
}

// Fetch warehouses for a company
export const fetchWarehouses = async (
  companyId: string
): Promise<{ warehouses: Warehouse[] }> => {
  const url = `${API_URL}/${companyId}/warehouses`;
  const response = await axios.get(url);
  return response.data;
};
